import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useCallback, useEffect, useState } from 'react';

export interface CoachConversation {
  id: string;
  subject_code: string;
  subject_label: string;
  diploma: string;
  section: string;
  created_at: string;
  last_message_at: string;
}

export interface CreateConversationParams {
  subjectCode: string;
  subjectLabel: string;
  diploma: string;
  section: string;
}

export function useCoachConversations() {
  const { session } = useAuth();
  const [conversations, setConversations] = useState<CoachConversation[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchConversations = useCallback(async () => {
    try {
      const userId = session?.user?.id;
      if (!userId) return;

      const { data, error } = await supabase
        .from('coach_conversations')
        .select('id, subject_code, subject_label, diploma, section, created_at, last_message_at')
        .eq('user_id', userId)
        .order('last_message_at', { ascending: false });

      if (error) throw error;

      setConversations(data || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    if (session?.user?.id) {
      fetchConversations();
    } else {
      setLoading(false);
    }
  }, [session?.user?.id, fetchConversations]);

  /**
   * Crée une nouvelle conversation coach pour une matière donnée.
   * Retourne l'ID de la conversation créée ou null en cas d'erreur.
   */
  const createConversation = async (
    params: CreateConversationParams
  ): Promise<string | null> => {
    try {
      const userId = session?.user?.id;
      if (!userId) {
        console.error('[createConversation] No user session');
        return null;
      }

      const { subjectCode, subjectLabel, diploma, section } = params;

      // Vérifier si une conversation existe déjà pour cette matière
      const existingConv = conversations.find(
        (c) => c.subject_code === subjectCode
      );
      if (existingConv) {
        console.log('[createConversation] Conversation already exists:', existingConv.id);
        return existingConv.id;
      }

      const now = new Date().toISOString();

      const { data, error } = await supabase
        .from('coach_conversations')
        .insert({
          user_id: userId,
          subject_code: subjectCode,
          subject_label: subjectLabel,
          diploma: diploma,
          section: section,
          created_at: now,
          last_message_at: now,
        })
        .select('id')
        .single();

      if (error) {
        console.error('[createConversation] Insert error:', error);
        return null;
      }

      if (!data?.id) {
        console.error('[createConversation] No ID returned');
        return null;
      }

      console.log('[createConversation] Created conversation:', data.id);

      // Rafraîchir la liste des conversations
      await fetchConversations();

      return data.id;
    } catch (error) {
      console.error('[createConversation] Unexpected error:', error);
      return null;
    }
  };

  /**
   * Retourne les subject_codes des conversations existantes.
   * Utile pour savoir quelles matières ont déjà une conversation.
   */
  const existingSubjectCodes = conversations.map((c) => c.subject_code);

  /**
   * Supprime une conversation et tous ses messages associés.
   * Retourne true si la suppression a réussi, false sinon.
   */
  const deleteConversation = async (conversationId: string): Promise<boolean> => {
    try {
      const userId = session?.user?.id;
      if (!userId) {
        console.error('[deleteConversation] No user session');
        return false;
      }

      // Supprimer d'abord les messages de la conversation
      const { error: messagesError } = await supabase
        .from('chat_messages')
        .delete()
        .eq('coach_conversation_id', conversationId);

      if (messagesError) {
        console.error('[deleteConversation] Error deleting messages:', messagesError);
        // On continue quand même pour supprimer la conversation
      }

      // Supprimer la conversation
      const { error } = await supabase
        .from('coach_conversations')
        .delete()
        .eq('id', conversationId)
        .eq('user_id', userId);

      if (error) {
        console.error('[deleteConversation] Error:', error);
        return false;
      }

      console.log('[deleteConversation] Deleted conversation:', conversationId);

      // Rafraîchir la liste
      await fetchConversations();

      return true;
    } catch (error) {
      console.error('[deleteConversation] Unexpected error:', error);
      return false;
    }
  };

  return {
    conversations,
    loading,
    createConversation,
    deleteConversation,
    existingSubjectCodes,
    refetch: fetchConversations,
  };
}
